import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { StarDisplay, StarRating } from "../components/StarRating";
import { BookCover } from "../components/BookCover";
import { toast } from "sonner";
import { ArrowLeft, BookOpen, Calendar, Heart, Check, Loader2 } from "lucide-react";

const OL = "https://openlibrary.org";

function authorPhoto(olid) {
  return `https://covers.openlibrary.org/a/olid/${olid}-L.jpg?default=false`;
}

function extractBio(bio) {
  if (!bio) return "";
  if (typeof bio === "string") return bio;
  if (bio?.value) return bio.value;
  return "";
}

export default function AuthorPage() {
  const { olid } = useParams(); // e.g. "OL23919A"
  const { user } = useAuth();

  const [author, setAuthor] = useState(null); // null=loading, false=not found
  const [works, setWorks] = useState([]);
  const [stats, setStats] = useState({ avg_rating: 0, review_count: 0 });
  const [reviews, setReviews] = useState([]);
  const [myRating, setMyRating] = useState(0);
  const [myText, setMyText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [following, setFollowing] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);

  const authorKey = `/authors/${olid}`;
  const authorName = author?.name || "";

  // Load follow status for the current viewer.
  useEffect(() => {
    if (!user || typeof user !== "object") { setFollowing(false); return; }
    let cancelled = false;
    api.get("/me/follows").then(({ data }) => {
      if (cancelled) return;
      setFollowing(data.some((f) => f.olid === olid));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [olid, user]);

  const toggleFollow = async () => {
    if (!user || typeof user !== "object") {
      toast.error("Sign in to follow an author");
      return;
    }
    setFollowBusy(true);
    try {
      if (following) {
        await api.delete(`/me/follows/${olid}`);
        setFollowing(false);
        toast.success(`Unfollowed ${authorName}`);
      } else {
        await api.post("/me/follows", { olid, name: authorName || "Unknown" });
        setFollowing(true);
        toast.success(`Following ${authorName}`);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setFollowBusy(false);
    }
  };

  // Load author bio + works from Open Library
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const a = await (await fetch(`${OL}/authors/${olid}.json`)).json();
        if (cancelled) return;
        if (!a?.name) { setAuthor(false); return; }
        setAuthor(a);
        const w = await (await fetch(`${OL}/authors/${olid}/works.json?limit=24`)).json();
        if (cancelled) return;
        setWorks(w?.entries || []);
      } catch {
        if (!cancelled) setAuthor(false);
      }
    })();
    return () => { cancelled = true; };
  }, [olid]);

  // Load author reviews + stats from our backend
  const loadReviews = () => {
    api.get(`/authors/${olid}/reviews`).then(({ data }) => {
      setReviews(data);
      if (user && typeof user === "object") {
        const mine = data.find((r) => r.user_id === user.id);
        if (mine) { setMyRating(mine.rating); setMyText(mine.text || ""); }
      }
    });
    api.get(`/authors/${olid}/stats`).then(({ data }) => setStats(data));
  };
  useEffect(loadReviews, [olid, user]);

  const submitReview = async (e) => {
    e.preventDefault();
    if (!myRating) { toast.error("Please choose a star rating"); return; }
    setSubmitting(true);
    try {
      await api.post(`/authors/${olid}/reviews`, {
        rating: myRating, text: myText, author_name: authorName || "Unknown",
      });
      toast.success("Review saved");
      loadReviews();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  const bio = useMemo(() => extractBio(author?.bio).split("---")[0].trim(), [author]);
  const birth = author?.birth_date;
  const death = author?.death_date;

  if (author === null) {
    return <div className="p-12 font-mono uppercase tracking-widest text-xs text-[#A3A3A3]">Loading author…</div>;
  }
  if (author === false) {
    return (
      <div className="p-12 max-w-3xl mx-auto">
        <div className="font-heading text-2xl text-[#525252]">Author not found on Open Library.</div>
        <Link to="/discover" className="font-mono uppercase tracking-widest text-xs text-[#0A0A0A] hover:underline mt-4 inline-block">Back to discover →</Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 py-12 fade-up" data-testid="author-page">
      <Link to="/discover" className="inline-flex items-center gap-1.5 font-mono uppercase tracking-widest text-[10px] text-[#525252] hover:text-[#0A0A0A] mb-8" data-testid="back-link">
        <ArrowLeft className="w-3 h-3" strokeWidth={1.5} /> Back to discover
      </Link>

      <div className="grid md:grid-cols-12 gap-10 md:gap-16">
        {/* Author photo + meta */}
        <div className="md:col-span-4">
          <div className="aspect-square overflow-hidden border border-[rgba(0,0,0,0.10)] bg-[#E8DFC1] shadow-book">
            <img
              src={authorPhoto(olid)}
              alt={authorName}
              loading="lazy"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
              className="w-full h-full object-cover"
            />
          </div>
          <dl className="mt-6 space-y-3 font-mono text-[10px] uppercase tracking-widest text-[#525252]">
            {birth && (
              <div className="flex items-baseline justify-between gap-3 border-b border-[rgba(0,0,0,0.06)] pb-2">
                <span className="text-[#A3A3A3]">Born</span>
                <span className="text-[#0A0A0A] normal-case font-body text-sm">{birth}</span>
              </div>
            )}
            {death && (
              <div className="flex items-baseline justify-between gap-3 border-b border-[rgba(0,0,0,0.06)] pb-2">
                <span className="text-[#A3A3A3]">Died</span>
                <span className="text-[#0A0A0A] normal-case font-body text-sm">{death}</span>
              </div>
            )}
            <div className="flex items-baseline justify-between gap-3 border-b border-[rgba(0,0,0,0.06)] pb-2">
              <span className="text-[#A3A3A3]">Works</span>
              <span className="text-[#0A0A0A] normal-case font-body text-sm">{works.length}+</span>
            </div>
            <div className="flex items-baseline justify-between gap-3 border-b border-[rgba(0,0,0,0.06)] pb-2">
              <span className="text-[#A3A3A3]">Source</span>
              <span className="text-[#0A0A0A] normal-case font-body text-sm">Open Library</span>
            </div>
          </dl>
        </div>

        {/* Bio + reviews */}
        <div className="md:col-span-8 space-y-10">
          <div>
            <div className="font-mono uppercase tracking-[0.25em] text-xs text-[#525252]">Author</div>
            <h1 className="font-heading text-4xl md:text-6xl text-[#0A0A0A] mt-3 tracking-tight leading-[1.0]" data-testid="author-name">{authorName}</h1>
            <div className="flex items-center justify-between gap-4 mt-5 flex-wrap">
              <div className="flex items-center gap-3">
                <StarDisplay value={stats.avg_rating} size={18} />
                <span className="font-mono text-xs text-[#525252]" data-testid="author-rating-summary">
                  {stats.avg_rating.toFixed(1)} · {stats.review_count} {stats.review_count === 1 ? "review" : "reviews"}
                </span>
              </div>
              <button
                onClick={toggleFollow}
                disabled={followBusy}
                aria-pressed={following}
                className={`inline-flex items-center gap-2 font-mono uppercase tracking-widest text-[11px] px-4 py-2 rounded-sm border transition-colors disabled:opacity-50 ${
                  following
                    ? "bg-[#0A0A0A] text-[#F2EAD3] border-[#0A0A0A] hover:bg-[#1F1F1F]"
                    : "bg-[#FAF5E6] text-[#0A0A0A] border-[rgba(10,10,10,0.18)] hover:bg-black/5"
                }`}
                data-testid="author-follow-toggle"
              >
                {followBusy
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} />
                  : following
                    ? <Check className="w-3.5 h-3.5" strokeWidth={1.5} />
                    : <Heart className="w-3.5 h-3.5" strokeWidth={1.5} />}
                {following ? "Following" : "Follow"}
              </button>
            </div>
          </div>

          {bio && (
            <p className="text-[#0A0A0A] text-base md:text-lg leading-relaxed max-w-2xl whitespace-pre-line" data-testid="author-bio">{bio}</p>
          )}

          {/* Notable works */}
          {works.length > 0 && (
            <div className="border-t border-[rgba(0,0,0,0.08)] pt-8">
              <h2 className="font-heading text-2xl text-[#0A0A0A] mb-6 tracking-tight">Notable works</h2>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-4">
                {works.slice(0, 10).map((w) => {
                  const workKey = w.key; // "/works/OLxxxW"
                  const coverId = w.covers && w.covers.find((c) => c && c > 0);
                  const coverUrl = coverId ? `https://covers.openlibrary.org/b/id/${coverId}-M.jpg` : "";
                  return (
                    <div key={workKey} className="space-y-2" data-testid={`work-${workKey.replace(/[^a-zA-Z0-9]/g,'_')}`}>
                      <BookCover src={coverUrl} title={w.title} author={authorName} />
                      <div className="font-heading text-xs text-[#0A0A0A] leading-tight line-clamp-2">{w.title}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Write review */}
          {user && typeof user === "object" && user.role === "sub_account" ? (
            <div className="border-t border-[rgba(0,0,0,0.08)] pt-8 border-l-2 border-l-[#0A0A0A] pl-5" data-testid="author-review-blocked-sub">
              <div className="font-mono uppercase tracking-[0.25em] text-xs text-[#525252] mb-1">Reviewing is off in sub-accounts</div>
              <p className="text-sm text-[#0A0A0A]">Public reviews can only be posted from your main account.</p>
            </div>
          ) : user && typeof user === "object" ? (
            <form onSubmit={submitReview} className="border-t border-[rgba(0,0,0,0.08)] pt-8 space-y-4" data-testid="author-review-form">
              <div className="font-mono uppercase tracking-[0.25em] text-xs text-[#525252]">Your review of this author</div>
              <StarRating value={myRating} onChange={setMyRating} size={22} testIdPrefix="author-my-star" />
              <textarea
                value={myText} onChange={(e) => setMyText(e.target.value)}
                placeholder="What do you think of this author's work overall?"
                rows={4}
                className="w-full bg-[#FAF5E6] border border-[rgba(0,0,0,0.10)] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black"
                data-testid="author-review-text"
              />
              <button disabled={submitting} className="bg-[#0A0A0A] text-white font-mono uppercase tracking-widest text-xs px-6 py-3 rounded-sm hover:bg-[#262626] disabled:opacity-60 transition-colors" data-testid="author-review-submit">
                {submitting ? "Saving…" : "Save review"}
              </button>
            </form>
          ) : (
            <div className="border-t border-[rgba(0,0,0,0.08)] pt-8">
              <Link to="/login" className="font-mono uppercase tracking-widest text-xs text-[#0A0A0A] hover:underline">Sign in to leave a review →</Link>
            </div>
          )}

          {/* Community reviews */}
          <div className="border-t border-[rgba(0,0,0,0.08)] pt-8">
            <h2 className="font-heading text-2xl text-[#0A0A0A] mb-6 tracking-tight">Community reviews</h2>
            {reviews.length === 0 ? (
              <div className="font-heading text-lg text-[#525252]">No reviews yet — be the first.</div>
            ) : (
              <ul className="space-y-6">
                {reviews.map((r) => (
                  <li key={r.id} className="border-b border-[rgba(0,0,0,0.06)] pb-6 last:border-0" data-testid={`author-review-${r.id}`}>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-[#E8DFC1] border border-[rgba(0,0,0,0.08)] flex items-center justify-center font-heading text-sm text-[#0A0A0A]">
                          {r.user_name?.[0]?.toUpperCase() || "?"}
                        </div>
                        <div>
                          <div className="font-heading text-base text-[#0A0A0A]">{r.user_name}</div>
                          <div className="font-mono text-[10px] uppercase tracking-widest text-[#A3A3A3]">{new Date(r.created_at).toLocaleDateString()}</div>
                        </div>
                      </div>
                      <StarDisplay value={r.rating} size={14} />
                    </div>
                    {r.text && <p className="text-[#0A0A0A] mt-3 leading-relaxed text-[15px]">{r.text}</p>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
